import { ValueFormatterParams } from '@ag-grid-community/core';
import { inject, Injectable } from '@angular/core';
import { first, uniqBy } from 'lodash-es';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import {
  batchCreateInvestigatorTransactionsMutation,
  CreateInvestigatorTransactionInput,
  Currency,
  GqlService,
  listPatientGroupsQuery,
  listPatientProtocolListQuery,
  listPatientProtocolSubTypesQuery,
  listPatientProtocolVersionsQuery,
  listPatientsQuery,
  listSitesQuery,
  PatientProtocolType,
  RemoveInvestigatorTransactionInput,
  UpdateInvestigatorTransactionInput,
} from '@shared/services/gql.service';
import { Option } from '@shared/types/components.type';
import { convertFilterToMap } from '@shared/utils';
import { Maybe, Utils } from '@shared/utils/utils';

import {
  InvestigatorTransactions,
  InvestigatorTransactionsDataRow,
  InvestigatorTransactionsFilterForm,
} from './investigator-transaction-form.model';

export type SiteMap = Pick<
  listSitesQuery,
  | 'address_line_1'
  | 'address_line_2'
  | 'address_line_3'
  | 'city'
  | 'contacts'
  | 'country'
  | 'currency'
  | 'id'
  | 'managed_by'
  | 'name'
  | 'site_no'
  | 'state'
  | 'zip'
>;

@Injectable({
  providedIn: 'root',
})
export class InvestigatorTransactionsService {
  gqlService = inject(GqlService);

  allPatientOptions$ = new BehaviorSubject<listPatientsQuery[]>([]);
  categoryOptions$ = new BehaviorSubject<listPatientProtocolSubTypesQuery[]>([]);
  countryOptions$ = new BehaviorSubject<Option[]>([]);
  initialFilters: InvestigatorTransactionsFilterForm = {
    countries: null,
    descriptions: null,
    end_activity_date: null,
    patient_group_ids: null,
    patient_ids: null,
    patient_protocol_sub_types: null,
    patient_protocol_version_ids: null,
    site_ids: null,
    sources: null,
    start_activity_date: null,
  };
  currentFilters$ = new BehaviorSubject<InvestigatorTransactionsFilterForm>(this.initialFilters);
  descriptionOptions$ = new BehaviorSubject<{ description: string }[]>([]);
  inMonthAdjustmentsFilter$ = new BehaviorSubject<boolean>(false);
  inMonthAdjustmentsFilterDateEnd = '';
  inMonthAdjustmentsFilterDateStart = '';
  inMonthAdjustmentsFilterSiteIds: string[] = [];
  inMonthAdjustmentsFilterVendorIds: string[] = [];
  lastSourceRefreshDate$ = new BehaviorSubject('');
  patientGroupMap = new Map<string, listPatientGroupsQuery>();
  patientGroupOptions$ = new BehaviorSubject<listPatientGroupsQuery[]>([]);
  patientOptions$ = new BehaviorSubject<listPatientsQuery[]>([]);
  protocolVersionOptions$ = new BehaviorSubject<listPatientProtocolVersionsQuery[]>([]);
  totalCost$ = new BehaviorSubject(0);
  totalItems$ = new BehaviorSubject(0);

  private _siteMap = new Map<string, SiteMap>();

  addInMonthAdjustmentsFilters(
    monthStart: string,
    monthEnd: string,
    vendorIds?: string[],
    siteId?: string
  ): void {
    if (!monthEnd) {
      return;
    }

    this.inMonthAdjustmentsFilterDateStart = monthStart;
    this.inMonthAdjustmentsFilterDateEnd = monthEnd;

    if (vendorIds?.length) {
      this.inMonthAdjustmentsFilterVendorIds = vendorIds;
    }

    if (siteId) {
      this.inMonthAdjustmentsFilterSiteIds = [siteId];
    }

    if (this.inMonthAdjustmentsFilterVendorIds.length) {
      this.setFilterSiteIds();
    }

    this.inMonthAdjustmentsFilter$.next(true);
  }

  dynamicPatientIdFilter(site_ids: string[], patient_ids: null | string[]): string[] {
    const filteredValues = this.allPatientOptions$
      .getValue()
      .filter((option) => site_ids.includes(option.site_id) || !site_ids.length);
    const arrWithoutDuplicatePatients: listPatientsQuery[] = [];

    filteredValues.forEach((value) => {
      if (
        !arrWithoutDuplicatePatients.includes(value) &&
        !arrWithoutDuplicatePatients.some((eachValue) => eachValue.id === value.id)
      ) {
        arrWithoutDuplicatePatients.push(value);
      }
    });
    this.patientOptions$.next(arrWithoutDuplicatePatients);

    return patient_ids
      ? this.patientOptions$
          .getValue()
          .map((option) => {
            return option.id;
          })
          .filter((option) => patient_ids.includes(option))
      : [];
  }

  private getCountryOptions(list: listSitesQuery[]): Option[] {
    return uniqBy(list, 'country')
      .map(({ country }) => ({
        label: Utils.getCountryName(country || ''),
        value: country || '',
      }))
      .filter(({ value }) => !!value)
      .sort(({ label }, { label: label2 }) => Utils.alphaNumSort(label, label2));
  }

  getInMonthAdjustmentsFilters(): [string, string, string[]] {
    return [
      this.inMonthAdjustmentsFilterDateStart,
      this.inMonthAdjustmentsFilterDateEnd,
      this.inMonthAdjustmentsFilterSiteIds,
    ];
  }

  private getPatientProtocolSubTypeId = (category: PatientProtocolType): string | undefined => {
    const categoryOptions = this.categoryOptions$.getValue();

    return categoryOptions.find(
      ({ name, patient_protocol_type }) =>
        patient_protocol_type === category &&
        ['Patient Invoiceable', 'Patient Visit', 'Site Invoiceable'].includes(name)
    )?.id;
  };

  getPatientTransactionsGridData(
    list: InvestigatorTransactions
  ): InvestigatorTransactionsDataRow[] {
    // eslint-disable-next-line complexity
    return list.map<InvestigatorTransactionsDataRow>((item) => ({
      activity_date: item.activity_date,
      contract_amount: item.amount?.contract_amount || 0,
      contract_curr: item.amount?.contract_currency as Currency,
      country: Utils.getCountryName(item.country || ''),
      currency: item.amount?.amount_currency as Currency,
      description: item.description,
      exchange_rate: Utils.exchangeRateFormatter({
        value: item.amount?.exchange_rate.rate || 0,
      } as ValueFormatterParams),
      external_patient_id: item.external_patient_id,
      id: item.id,
      investigator_name: this.getPrincipalInvestigatorName(item.site_id || ''),
      mode: 'static',
      patient_group_id: this.patientGroupMap.get(item.patient_group_id || '')?.name || '',
      patient_protocol_category: item.patient_protocol_category || '',
      patient_protocol_version_name: item.patient_protocol_version_name || '',
      site_address: this.getSiteAddress(this.siteMap.get(item.site_id || '')),
      site_budget_note: item.site_budget_note || '',
      site_budget_version_name: item.site_budget_version_name || '',
      site_name: this.siteMap.get(item.site_id || '')?.name || '',
      site_no: this.siteMap.get(item.site_id || '')?.site_no || '',
      source_created_date: item.source_created_date,
      source_name: item.source?.name || '',
      source_type: item.source?.id || null,
      total_cost: item.amount?.amount || 0,
    }));
  }

  getPrincipalInvestigatorName(siteId: string): string {
    const principalInvestigator = first(this.siteMap.get(siteId)?.contacts);

    return principalInvestigator
      ? `${principalInvestigator.given_name} ${principalInvestigator.family_name}`
      : '';
  }

  private getSafeAddressName(address: Maybe<string>, suffix = ','): string {
    return address ? `${address}${suffix}` : '';
  }

  getSiteAddress(site?: SiteMap): string {
    const citySubStr = this.getSafeAddressName(site?.city);

    const address = `${this.getSafeAddressName(site?.address_line_1)} ${this.getSafeAddressName(
      site?.address_line_2
    )} ${this.getSafeAddressName(site?.address_line_3)}`.trim();

    return site
      ? `${address} ${citySubStr} ${site.state || ''} ${site.zip || ''} ${Utils.getCountryName(
          site.country || ''
        )}`.trim()
      : Utils.zeroHyphen;
  }

  removeTransaction(transactionId: string, note: string, documentsPaths: string[]) {
    return firstValueFrom(
      this.gqlService.batchRemoveInvestigatorTransactions$({
        ids: [transactionId],
        note,
        supporting_doc_bucket_keys: documentsPaths,
      } as RemoveInvestigatorTransactionInput)
    );
  }

  resetInMonthAdjustmentsFilters(): void {
    this.inMonthAdjustmentsFilterDateStart = '';
    this.inMonthAdjustmentsFilterDateEnd = '';
    this.inMonthAdjustmentsFilterVendorIds = [];
    this.inMonthAdjustmentsFilterSiteIds = [];
  }

  saveTransaction(
    transactions: InvestigatorTransactionsDataRow[],
    note: string,
    documentsPaths: string[]
  ): Promise<GraphqlResponse<batchCreateInvestigatorTransactionsMutation[]>> {
    const inputs: CreateInvestigatorTransactionInput = {
      note,
      supporting_doc_bucket_keys: documentsPaths,
      transactions: transactions.map((transaction) => ({
        amount: transaction.contract_amount,
        completion_date: transaction.activity_date,
        description: transaction.description ?? '',
        external_patient_id: transaction.external_patient_id,
        patient_protocol: {
          patient_group_id: transaction.patient_group_id ?? null,
          patient_protocol_sub_type_id:
            this.getPatientProtocolSubTypeId(
              transaction.patient_protocol_category as PatientProtocolType
            ) ?? '',
          patient_protocol_type: transaction.patient_protocol_category as PatientProtocolType,
          patient_protocol_version_id: transaction.patient_protocol_version_name ?? '',
        },
        site_id: transaction.site_no,
        source_created_date: transaction.source_created_date,
      })),
    };

    return firstValueFrom(this.gqlService.batchCreateInvestigatorTransactions$(inputs));
  }

  private setFilterSiteIds(): void {
    const siteIds = this.siteIdsByVendors(this.inMonthAdjustmentsFilterVendorIds);

    if (!siteIds.length) {
      return;
    }

    this.inMonthAdjustmentsFilterSiteIds = siteIds;
  }

  setInMonthAdjustmentsFilters(): void {
    if (!this.inMonthAdjustmentsFilterDateEnd) {
      return;
    }

    if (this.inMonthAdjustmentsFilterVendorIds.length) {
      this.setFilterSiteIds();
    }
  }

  setOptions({
    descriptionList,
    patientGroupList,
    patientList,
    patientProtocolSubTypeList,
    protocolVersionList,
    siteList,
  }: {
    descriptionList: GraphqlResponse<listPatientProtocolListQuery>;
    patientGroupList: GraphqlResponse<listPatientGroupsQuery[]>;
    patientList: GraphqlResponse<listPatientsQuery[]>;
    patientProtocolSubTypeList: GraphqlResponse<listPatientProtocolSubTypesQuery[]>;
    protocolVersionList: GraphqlResponse<listPatientProtocolVersionsQuery[]>;
    siteList: GraphqlResponse<listSitesQuery[]>;
  }) {
    this.patientOptions$.next(
      (patientList.data || []).sort(
        ({ external_patient_id }, { external_patient_id: external_patient_id2 }) =>
          Utils.alphaNumSort(external_patient_id, external_patient_id2)
      )
    );
    this.allPatientOptions$.next(this.patientOptions$.getValue());

    this.descriptionOptions$.next(
      descriptionList.data?.descriptions?.map((value) => ({ description: value || '' })) || []
    );
    this.patientGroupOptions$.next(
      (patientGroupList.data || []).sort(({ name }, { name: name2 }) =>
        Utils.alphaNumSort(name, name2)
      )
    );
    this.protocolVersionOptions$.next(
      (protocolVersionList.data || []).sort(({ name }, { name: name2 }) =>
        Utils.alphaNumSort(name, name2)
      )
    );
    this.categoryOptions$.next(
      (patientProtocolSubTypeList.data || []).sort(({ name }, { name: name2 }) =>
        Utils.alphaNumSort(name, name2)
      )
    );
    this.countryOptions$.next(this.getCountryOptions(siteList?.data || []));
  }

  private siteIdsByVendors(vendorIds: string[]): string[] {
    const sitesByVendor = [...this.siteMap].filter(([, site]) => {
      return vendorIds.includes(site.managed_by.id);
    });

    const siteIds = sitesByVendor.map(([siteId]) => siteId);

    return siteIds;
  }

  get siteMap(): Map<string, SiteMap> {
    return this._siteMap;
  }

  set siteMap(value: SiteMap[]) {
    this._siteMap = convertFilterToMap<SiteMap>(value);
  }

  updateTransactions(
    transactions: InvestigatorTransactionsDataRow[],
    note: string,
    documentsPaths: string[]
  ) {
    const inputs: UpdateInvestigatorTransactionInput = {
      note: note,
      supporting_doc_bucket_keys: documentsPaths,
      transactions: transactions.map((transaction) => ({
        amount: transaction.contract_amount,
        completion_date: transaction.activity_date,
        description: transaction.description,
        external_patient_id: transaction.external_patient_id,
        id: transaction.id,
        patient_protocol: {
          patient_group_id: transaction.patient_group_id ?? null,
          patient_protocol_sub_type_id:
            this.getPatientProtocolSubTypeId(
              transaction.patient_protocol_category as PatientProtocolType
            ) ?? '',
          patient_protocol_type: transaction.patient_protocol_category as PatientProtocolType,
          patient_protocol_version_id: transaction.patient_protocol_version_name as string,
        },
        site_id: transaction.site_no,
      })),
    };

    return firstValueFrom(this.gqlService.batchUpdateInvestigatorTransactions$(inputs));
  }
}
